import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { listMediaAssets, uploadMediaAsset } from '@/services/media';
import type { MediaAsset } from '@/domain/types';

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif']);

function isFile(a: MediaAsset): boolean {
  return (
    a.assetType === 'product_file' &&
    !IMAGE_EXTS.has(a.fileExtension?.toLowerCase() ?? '') &&
    !a.mimeType?.startsWith('image/')
  );
}

function fmtSize(bytes: number | null) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function fileIcon(ext: string) {
  const e = ext.toLowerCase();
  if (['zip', 'gz', 'tar', '7z', 'rar'].includes(e)) return { icon: 'ri-file-zip-line',      color: 'text-amber-500 bg-amber-50 border-amber-200' };
  if (['json', 'xml', 'yaml', 'yml'].includes(e))    return { icon: 'ri-code-line',           color: 'text-sky-500 bg-sky-50 border-sky-200' };
  if (e === 'pdf')                                    return { icon: 'ri-file-pdf-line',       color: 'text-red-500 bg-red-50 border-red-200' };
  if (['js', 'ts', 'css', 'html', 'php'].includes(e)) return { icon: 'ri-code-s-slash-line',  color: 'text-violet-500 bg-violet-50 border-violet-200' };
  if (['exe', 'dmg', 'pkg', 'deb'].includes(e))      return { icon: 'ri-install-line',        color: 'text-rose-500 bg-rose-50 border-rose-200' };
  return { icon: 'ri-file-3-line', color: 'text-surface-500 bg-surface-50 border-surface-200' };
}

type Tab = 'library' | 'upload';

export interface PickedFile {
  type: 'asset';
  asset: MediaAsset;
}

interface Props {
  onPick: (result: PickedFile | File) => void;
  onClose: () => void;
  title?: string;
}

export function FilePicker({ onPick, onClose, title = 'Choose Product File' }: Props) {
  const [tab, setTab]           = useState<Tab>('library');
  const [assets, setAssets]     = useState<MediaAsset[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [picked, setPicked]     = useState<MediaAsset | null>(null);

  // Upload tab state
  const [isDragging, setIsDragging]   = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading]     = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter  = useRef(0);

  const loadAssets = () =>
    listMediaAssets()
      .then((all) => setAssets(all.filter(isFile)))
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { loadAssets(); }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const filtered = assets.filter((a) =>
    !search || a.displayName.toLowerCase().includes(search.toLowerCase()) ||
    (a.productName ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  // ── Upload handlers ──────────────────────────────────────────────────────────

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setPendingFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    e.target.value = '';
  };

  const handleUploadAndPick = async () => {
    if (!pendingFile) return;
    setUploadError('');
    setUploading(true);
    try {
      await uploadMediaAsset(pendingFile, 'product-files');
      await loadAssets();
      onPick(pendingFile);
      onClose();
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed.');
      setUploading(false);
    }
  };

  const handleConfirm = () => {
    if (tab === 'library' && picked) { onPick({ type: 'asset', asset: picked }); onClose(); }
    if (tab === 'upload' && pendingFile) handleUploadAndPick();
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden" style={{ maxHeight: '88vh' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                <i className="ri-file-zip-line text-amber-600" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-surface-900">{title}</h2>
                <p className="text-xs text-surface-400">{assets.length} file{assets.length !== 1 ? 's' : ''} in library</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 transition-colors">
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-surface-200 shrink-0 px-6">
            {([
              ['library', 'ri-folder-3-line',        'File Library'],
              ['upload',  'ri-upload-cloud-2-line',   'Upload New'],
            ] as [Tab, string, string][]).map(([key, icon, label]) => (
              <button
                key={key}
                onClick={() => { setTab(key); setUploadError(''); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  tab === key
                    ? 'border-brand-600 text-brand-600'
                    : 'border-transparent text-surface-500 hover:text-surface-700'
                }`}
              >
                <i className={icon} /> {label}
              </button>
            ))}
          </div>

          {/* ── Library tab ── */}
          {tab === 'library' && (
            <>
              <div className="px-6 py-3 border-b border-surface-100 shrink-0">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm" />
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus
                    placeholder="Search files…"
                    className="w-full pl-9 pr-4 py-2 rounded-lg border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-surface-50" />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <i className="ri-loader-4-line animate-spin text-3xl text-surface-300" />
                    <p className="text-sm text-surface-400">Loading files…</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-surface-100 flex items-center justify-center">
                      <i className="ri-file-3-line text-2xl text-surface-400" />
                    </div>
                    <p className="text-sm font-medium text-surface-600">
                      {assets.length === 0 ? 'No product files in library yet' : 'No files match your search'}
                    </p>
                    <button onClick={() => setTab('upload')} className="text-xs text-brand-600 hover:underline flex items-center gap-1">
                      <i className="ri-upload-cloud-2-line" /> Upload a file
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-surface-50">
                    {filtered.map((asset) => {
                      const isSelected = picked?.id === asset.id;
                      const { icon, color } = fileIcon(asset.fileExtension ?? '');
                      return (
                        <button
                          key={asset.id}
                          type="button"
                          onClick={() => setPicked(asset)}
                          className={`w-full flex items-center gap-4 px-6 py-3.5 text-left transition-colors ${
                            isSelected ? 'bg-brand-50' : 'hover:bg-surface-50'
                          }`}
                        >
                          {/* File icon */}
                          <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${color}`}>
                            <i className={`${icon} text-lg`} />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-surface-900 truncate">{asset.displayName}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-surface-400 uppercase font-medium">.{asset.fileExtension || 'file'}</span>
                              <span className="text-surface-300">·</span>
                              <span className="text-xs text-surface-400">{fmtSize(asset.fileSize)}</span>
                              {asset.productName && (
                                <>
                                  <span className="text-surface-300">·</span>
                                  <span className="text-xs text-surface-400 truncate">{asset.productName}</span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Selected indicator */}
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                            isSelected ? 'border-brand-500 bg-brand-500' : 'border-surface-300'
                          }`}>
                            {isSelected && <i className="ri-check-line text-white text-[10px] font-bold" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Upload tab ── */}
          {tab === 'upload' && (
            <div className="flex-1 flex flex-col items-center justify-center p-8 min-h-0 overflow-y-auto">
              {pendingFile ? (
                /* File selected — confirm screen */
                <div className="w-full max-w-md space-y-5">
                  <div className="flex items-center gap-4 p-5 rounded-2xl border border-surface-200 bg-surface-50">
                    <div className={`w-14 h-14 rounded-2xl border flex items-center justify-center shrink-0 ${fileIcon(pendingFile.name.split('.').pop() ?? '').color}`}>
                      <i className={`${fileIcon(pendingFile.name.split('.').pop() ?? '').icon} text-2xl`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-surface-900 truncate">{pendingFile.name}</p>
                      <p className="text-xs text-surface-400 mt-0.5">{fmtSize(pendingFile.size)}</p>
                    </div>
                    <button onClick={() => setPendingFile(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-200 text-surface-400 transition-colors shrink-0">
                      <i className="ri-close-line" />
                    </button>
                  </div>

                  {uploadError && (
                    <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                      <i className="ri-error-warning-line shrink-0" /> {uploadError}
                    </div>
                  )}

                  <p className="text-xs text-surface-400 text-center">
                    This file will be uploaded to your media library and set as the product file.
                  </p>
                </div>
              ) : (
                /* Drop zone */
                <div
                  onDragEnter={(e) => { e.preventDefault(); dragCounter.current++; setIsDragging(true); }}
                  onDragOver={(e) => e.preventDefault()}
                  onDragLeave={(e) => { e.preventDefault(); dragCounter.current--; if (dragCounter.current === 0) setIsDragging(false); }}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full max-w-lg rounded-2xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center gap-5 py-14 px-8 text-center ${
                    isDragging
                      ? 'border-brand-400 bg-brand-50'
                      : 'border-surface-300 bg-surface-50/50 hover:border-brand-400 hover:bg-brand-50/30'
                  }`}
                >
                  {isDragging ? (
                    <>
                      <div className="w-16 h-16 rounded-2xl bg-brand-100 flex items-center justify-center animate-bounce">
                        <i className="ri-upload-cloud-2-line text-3xl text-brand-600" />
                      </div>
                      <p className="text-base font-semibold text-brand-600">Drop your file here</p>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 rounded-2xl bg-white border border-surface-200 shadow-sm flex items-center justify-center">
                        <i className="ri-upload-cloud-2-line text-3xl text-surface-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-surface-700 mb-1">Drag & drop your file here</p>
                        <p className="text-xs text-surface-400 mb-4">or click to browse — ZIP, JSON, PDF, JS, any format</p>
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          {['ZIP', 'JSON', 'PDF', 'JS', 'PHP', 'Any'].map((fmt) => (
                            <span key={fmt} className="px-2 py-0.5 rounded-md bg-white border border-surface-200 text-xs text-surface-500 font-medium">{fmt}</span>
                          ))}
                        </div>
                      </div>
                      <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                        className="px-6 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors shadow-sm">
                        <i className="ri-folder-open-line mr-2" />Browse Files
                      </button>
                    </>
                  )}
                </div>
              )}

              <input ref={fileInputRef} type="file" onChange={handleFileInput} className="hidden" />
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-surface-200 flex items-center justify-between shrink-0 bg-surface-50/50">
            <div className="flex items-center gap-3 min-w-0">
              {tab === 'library' && picked ? (
                <>
                  <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${fileIcon(picked.fileExtension ?? '').color}`}>
                    <i className={`${fileIcon(picked.fileExtension ?? '').icon} text-sm`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-surface-700">Selected</p>
                    <p className="text-[11px] text-surface-400 truncate max-w-[220px]">{picked.displayName}</p>
                  </div>
                </>
              ) : tab === 'upload' && pendingFile ? (
                <p className="text-xs text-surface-600 flex items-center gap-1.5">
                  <i className="ri-file-3-line text-brand-500" /> Ready to upload: <span className="font-semibold truncate max-w-[180px]">{pendingFile.name}</span>
                </p>
              ) : (
                <p className="text-xs text-surface-400">
                  {tab === 'library' ? 'Click a file to select it' : 'Drop or browse a file to upload'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={(tab === 'library' && !picked) || (tab === 'upload' && !pendingFile) || uploading}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors flex items-center gap-2"
              >
                {uploading && <i className="ri-loader-4-line animate-spin" />}
                {tab === 'upload' && pendingFile ? (uploading ? 'Uploading…' : 'Upload & Use') : 'Use This File'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
